import {
  EnvironmentProviders,
  inject,
  Injectable,
  makeEnvironmentProviders,
  provideAppInitializer
} from '@angular/core'
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router'

import { filter } from 'rxjs'

import { TranslateService } from '@ngx-translate/core'

import { PageTitleService } from './page-title.service'
import { DataModelStore } from '../api/entities/data-model.store'
import { entityTypeLabel } from '../api/entities/entity-type-label'

/**
 * Applies static `data.titleKey` (and entity-list type labels) on every
 * `NavigationEnd`. Record pages (entity/incident/centerline detail) own their
 * titles via {@link PageTitleService.setName} once the payload arrives — this
 * binder does not invent URL-only titles for them.
 *
 * Root services are lazy; {@link providePageTitle} forces construction at boot.
 */
@Injectable({ providedIn: 'root' })
export class PageTitleBinder {
  private readonly router = inject(Router)
  private readonly pageTitle = inject(PageTitleService)
  private readonly translate = inject(TranslateService)
  private readonly dataModelStore = inject(DataModelStore)

  constructor() {
    // Seed the env-prefixed fallback before the first NavigationEnd so a deep
    // link onto a record page is not stuck on the bare index.html title.
    this.pageTitle.setName(null)

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.apply(this.router.routerState.snapshot.root))
  }

  private apply(root: ActivatedRouteSnapshot): void {
    const titleKey = deepestTitleKey(root)
    if (titleKey) {
      this.pageTitle.setName(this.translate.instant(titleKey))
      return
    }

    const typeSlug = entityListTypeSlug(root)
    if (typeSlug) {
      this.pageTitle.setName(entityTypeLabel(typeSlug, this.dataModelStore, this.translate))
      return
    }

    // Entity / incident / centerline detail pages set the title when the
    // record loads. Leave whatever is there (often the list title) until then.
    if (isRecordOwnedRoute(root)) return

    this.pageTitle.setName(null)
  }
}

/** Start listening from app boot — root injectables are otherwise lazy. */
export const providePageTitle = (): EnvironmentProviders =>
  makeEnvironmentProviders([
    provideAppInitializer(() => {
      inject(PageTitleBinder)
    })
  ])

function deepestTitleKey(root: ActivatedRouteSnapshot): string | null {
  let key: string | null = null
  let current: ActivatedRouteSnapshot | null = root
  while (current) {
    const value = current.data['titleKey']
    if (typeof value === 'string' && value) key = value
    current = current.firstChild
  }
  return key
}

/** `/admin/entities/:typeSlug` list (no `:entityId`). */
function entityListTypeSlug(root: ActivatedRouteSnapshot): string | null {
  let typeSlug: string | null = null
  let hasEntityId = false
  let current: ActivatedRouteSnapshot | null = root
  while (current) {
    const slug = current.paramMap.get('typeSlug')
    if (slug) typeSlug = slug
    if (current.paramMap.get('entityId')) hasEntityId = true
    current = current.firstChild
  }
  return typeSlug && !hasEntityId ? typeSlug : null
}

function isRecordOwnedRoute(root: ActivatedRouteSnapshot): boolean {
  let current: ActivatedRouteSnapshot | null = root
  while (current) {
    if (
      current.paramMap.get('entityId') ||
      current.paramMap.get('incidentId') ||
      current.paramMap.get('sheetId')
    ) {
      return true
    }
    current = current.firstChild
  }
  return false
}
